import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Box, CircularProgress, Typography, Alert, Button } from '@mui/material';
import axios from 'axios';
import API_BASE from '../apiBase';
import { auth } from '../firebase';
import { signOut } from 'firebase/auth';
import { setStoredUser } from '../utils/userStorage';


function OAuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState('');

  const [isLoading, setIsLoading] = useState(true);
  const provider = location.pathname.split('/').pop();

  useEffect(() => {
    const handleCallback = async () => {
      try {
        console.log('🔄 Starting OAuth callback process...');
        const params = new URLSearchParams(location.search);
        const code = params.get('code');
        const state = params.get('state');
        const token = params.get('token');
        const expectedState = localStorage.getItem('oauth_state');

        if (provider === 'github' || provider === 'microsoft') {
          console.log(`ℹ️  ${provider} callback route is no longer used. Redirecting.`);
          if (auth.currentUser) {
            navigate('/dashboard');
          } else {
            navigate('/login');
          }
          setIsLoading(false);
          return;
        }

        console.log(`ℹ️  OAuth Provider: ${provider}`);
        console.log(`ℹ️  State: ${state}`);
        console.log(`ℹ️  Code present: ${!!code}`);
        console.log(`ℹ️  Token present: ${!!token}`);

        // Check if we have a token directly (new flow) or need to exchange code (old flow)
        if (token) {
          console.log('✅ Token received directly from backend redirect');
          // Continue with token processing
        } else if (code) {
          console.log('🔄 Code received, exchanging for token...');
          // Old flow - exchange code for token
          if (!state || state !== expectedState) {
            console.error('❌ State mismatch or missing. Possible CSRF attack.');
            setError('State mismatch or missing. Possible CSRF attack.');
            setIsLoading(false);
            return;
          }
          
          try {
            const response = await axios.post(`${API_BASE}/api/oauth/${provider}/callback`, { code, state });
            if (response.data.user) {
              // Bootstrap user and refresh profile
              try {
                await axios.post(`${API_BASE}/api/users/bootstrap`);
                const { data: me } = await axios.get(`${API_BASE}/api/users/me`);
                if (me) {
                  setStoredUser(me);
                } else {
                  throw new Error('User profile could not be fetched after OAuth login.');
                }
              } catch (authSetupError) {
                console.error('Failed to setup user profile after OAuth:', authSetupError);
                try { await signOut(auth); } catch {}
                setError('Authentication succeeded but profile setup failed. Please try again.');
                setIsLoading(false);
                return;
              }
              // Check if user needs to complete profile
              const user = response.data.user || {};
              const needsProfileUpdate = !user.phone || user.phone.trim() === '' || 
                !user.address || 
                !user.address.street1 || 
                !user.address.city || 
                !user.address.state || 
                !user.address.country;

              if (needsProfileUpdate) {
                navigate('/complete-profile', { 
                  state: { 
                    user: user, 
                  } 
                });
                return;
              }
              
              navigate('/dashboard');
              return;
            }
          } catch (error) {
            console.error('❌ Failed to exchange code for token:', error);
            setError('Failed to complete authentication: ' + (error?.response?.data?.error || error.message));
            setIsLoading(false);
            return;
          }
        } else {
          console.error('❌ No authorization code or token received');
          setError('No authorization code or token received from provider');
          setIsLoading(false);
          return;
        }

        console.log('🔄 Checking for token in URL parameters...');
        const urlParams = new URLSearchParams(location.search);
        const userParam = urlParams.get('user');
        const isNewUser = urlParams.get('is_new_user') === 'true';
        
        // Parse user data if available
        let userData = null;
        if (userParam) {
          try {
            userData = JSON.parse(decodeURIComponent(userParam));
          } catch (e) {
            console.warn('⚠️  Could not parse user data from URL:', e);
          }
        }
        
        const data = { token, user: userData, is_new_user: isNewUser };
        console.log('✅ Successfully received token from URL parameters');

        // Optionally store user info
        if (data.user) {
          // Bootstrap user and refresh profile
          try {
            await axios.post(`${API_BASE}/api/users/bootstrap`);
            const { data: me } = await axios.get(`${API_BASE}/api/users/me`);
            if (me) {
              setStoredUser(me);
            } else {
              throw new Error('User profile could not be fetched after OAuth login.');
            }
          } catch (authSetupError) {
            console.error('Failed to setup user profile after OAuth:', authSetupError);
            try { await signOut(auth); } catch {}
            setError('Authentication succeeded but profile setup failed. Please try again.');
            setIsLoading(false);
            return;
          }
        }

        // 2. Wait a tick to ensure persistence
        console.log('⏳ Waiting 200ms to ensure token persistence...');
        await new Promise(resolve => setTimeout(resolve, 200));
        console.log('✅ Wait complete, checking profile completeness.');

        // 3. Check if user needs to complete profile (frontend check)
        const user = data.user;
        const needsProfileUpdate = !user.phone || user.phone.trim() === '' || 
          !user.address || 
          !user.address.street1 || 
          !user.address.city || 
          !user.address.state || 
          !user.address.country;

        if (needsProfileUpdate) {
          console.log('ℹ️  User needs to complete profile, redirecting to complete-profile page');
          navigate('/complete-profile', { 
            state: { 
              user: user, 
            } 
          });
          return;
        }

        // 4. Navigate to dashboard - Layout component will handle project checking
        console.log('✅ Authentication complete, redirecting to dashboard');
        navigate('/dashboard');
      } catch (error) {
        console.error('❌ OAuth callback failed:', error);
        console.error('Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
        setError('Failed to complete authentication: ' + (error?.response?.data?.error || error.message));
      } finally {
        setIsLoading(false);
      }
    };

    handleCallback();
  }, [location, navigate, provider]);



  const handleRetry = () => {
    setIsLoading(true);
    setError('');
    // Retry the callback process
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const state = params.get('state');
    if (provider === 'github' || provider === 'microsoft') {
      navigate('/login');
      return;
    }
    
    if (code) {
      axios.post(`${API_BASE}/api/oauth/${provider}/callback`, { code, state })
        .then(response => {
          if (response.data?.user) {
            setStoredUser(response.data.user);
          }
          navigate('/dashboard');
        })
        .catch(error => {
          setError('Retry failed: ' + (error?.response?.data?.error || error.message));
          setIsLoading(false);
        });
    }
  };

  if (error) {
    return (
      <Box sx={{ bgcolor: "#1f1f1f", minHeight: "100vh", color: "#fff" }}>
      <Container>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          <Button variant="contained" onClick={handleRetry} sx={{ mt: 2 }}>
            Retry Authentication
          </Button>
        </Box>
      </Container>
      </Box>
    );
  }



  if (isLoading) {
    return (
      <Box sx={{ bgcolor: "#1f1f1f", minHeight: "100vh", color: "#fff" }}>
      <Container>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
          }}
        >
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Completing authentication...
          </Typography>
        </Box>
      </Container>
      </Box>
    );
  }

  // Fallback UI if neither error nor project prompt is shown
  return (
    <Box sx={{ bgcolor: "#1f1f1f", minHeight: "100vh", color: "#fff" }}>
    <Container>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>
          Nothing to show. You may already be logged in or something went wrong silently.
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/dashboard')} sx={{ mt: 2 }}>
          Go to Dashboard
        </Button>
        <Button variant="text" onClick={handleRetry} sx={{ mt: 1 }}>
          Retry Authentication
        </Button>
      </Box>
    </Container>
    </Box>
  );
}

export default OAuthCallback; 